import React, { useState, useEffect, useMemo } from 'react';
import { 
  TextField, 
  Box, 
  Tabs, 
  Tab, 
  Typography, 
  Grid, 
  Pagination, 
  Avatar, 
  Button, 
  Collapse,
  List,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Feed } from "semantic-ui-react";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { SectionTitle } from "components/shared/Typography";
import BoxShadow from "components/shared/BoxShadow";

const ITEMS_PER_PAGE = 10;
const ALL_ACTIVITIES = "ALL";
const ALL_USERS = "ALL_USERS";
const ACTIVITY_FILTERS = [
  { value: ALL_ACTIVITIES, label: "All" },
  { value: "EDIT", label: "Edit" },
  { value: "EMAIL_SEND", label: "Email" },
  //other non used filters
  // { value: "CREATE", label: "Create" },
  // { value: "CERTIFICATE_CREATION", label: "Certificate Creation" },
  // { value: "REQUEST_CERTIFICATE", label: "Request Certificate" },
  // { value: "DELETE_CERTIFICATE", label: "Delete Certificate" },
];

export default function ActivitiesList({ activities = [], vendorLabel = "Vendor" }) {
  const [filteredActivities, setFilteredActivities] = useState(activities);
  const [searchTerm, setSearchTerm] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [currentFilter, setCurrentFilter] = useState(ALL_ACTIVITIES);
  const [selectedUser, setSelectedUser] = useState(ALL_USERS);
  const [showActivityDetail, setShowActivityDetail] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  
  // Function to reset all filters
  const resetFilters = () => {
    setSearchTerm("");
    setFromDate("");
    setToDate("");
    setCurrentFilter(ALL_ACTIVITIES);
    setSelectedUser(ALL_USERS);
    setCurrentPage(1);
  };
  
  // Extract unique users from activities
  const uniqueUsers = useMemo(() => {
    const uniqueUsersSet = new Set();
    activities.forEach(activity => {
      if (activity.userFullName) {
        uniqueUsersSet.add(activity.userFullName);
      }
    });
    return [ALL_USERS, ...Array.from(uniqueUsersSet)];
  }, [activities]);
  
  // Apply all filters whenever any filter changes
  useEffect(() => {
    let result = activities;
    
    // Filter by search term
    if (searchTerm) {
      result = result.filter(activity => {
        const field = JSON.parse(activity.field || "{}");
        const activityText = JSON.stringify(field).toLowerCase();
        const userNameMatch = activity.userFullName?.toLowerCase().includes(searchTerm.toLowerCase());
        const contentMatch = activityText.includes(searchTerm.toLowerCase());
        return userNameMatch || contentMatch;
      });
    }
    
    // Filter by date range
    if (fromDate) {
      const fromTimestamp = new Date(fromDate).getTime();
      result = result.filter(activity => new Date(activity.createdAt).getTime() >= fromTimestamp);
    }
    
    if (toDate) {
      // Set to end of day for the "to" date
      const toTimestamp = new Date(toDate);
      toTimestamp.setHours(23, 59, 59, 999);
      result = result.filter(activity => new Date(activity.createdAt).getTime() <= toTimestamp.getTime());
    }
    
    // Filter by activity type
    if (currentFilter !== ALL_ACTIVITIES) {
      result = result.filter(activity => activity.action === currentFilter);
    }
    
    // Filter by user
    if (selectedUser !== ALL_USERS) {
      result = result.filter(activity => activity.userFullName === selectedUser);
    }
    
    setFilteredActivities(result);
    setCurrentPage(1); // Reset to first page when filters change
  }, [activities, searchTerm, fromDate, toDate, currentFilter, selectedUser]);
  
  // Check if any filters are active
  const hasActiveFilters = searchTerm || fromDate || toDate || currentFilter !== ALL_ACTIVITIES || selectedUser !== ALL_USERS;
  
  const handleFilterChange = (event, newValue) => {
    setCurrentFilter(newValue);
  };
  
  const handleUserChange = (event) => {
    setSelectedUser(event.target.value);
  };
  
  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };
  
  // Calculate pagination
  const totalPages = Math.ceil(filteredActivities.length / ITEMS_PER_PAGE);
  const paginatedActivities = filteredActivities.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );
  
  const getActivityHeader = (field, activity, index) => {
    return (
      <Feed.Summary style={{ display: "flex", alignItems: "baseline", width: "100%" }}>
        <Box>
          <span style={{ fontWeight: "600" }}>{activity.userFullName}</span>
          {field.message ? ` ${field.message}` : activity.action !== "CREATE" ? ` updated ${vendorLabel}'s information` : ` created ${vendorLabel}`}
          {!!field.details && (
            <div>
              <Button
                size="small"
                sx={{ ml: "auto", flexShrink: 0 }}
                onClick={() =>
                  setShowActivityDetail({
                    ...showActivityDetail,
                    [index]: !showActivityDetail[index],
                  })
                }
              >
                {`${!!showActivityDetail[index] ? "Hide" : "Show"} details`}
              </Button>
            </div>
          )}
        </Box>
      </Feed.Summary>
    );
  };
  
  return (
    <BoxShadow>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <SectionTitle sx={{ mb: 0 }}>Activities</SectionTitle>
          <Button
            variant="outlined"
            startIcon={<RestartAltIcon />}
            size="small"
            disabled={!hasActiveFilters}
            onClick={resetFilters}
          >
            Reset filters
          </Button>
      </Box>
      
      {/* Search, User and Date Filters */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            label="Search activities"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth size="small">
            <InputLabel id="user-filter-label">Filter by User</InputLabel>
            <Select
              labelId="user-filter-label"
              id="user-filter"
              value={selectedUser}
              label="Filter by User"
              onChange={handleUserChange}
            >
              <MenuItem value={ALL_USERS}>All Users</MenuItem>
              {uniqueUsers.filter(user => user !== ALL_USERS).map((user) => (
                <MenuItem key={user} value={user}>
                  {user}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                id="from-date"
                label="From Date"
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                size="small"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                id="to-date"
                label="To Date"
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                size="small"
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      
      {/* Activity Type Filters */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={currentFilter} 
          onChange={handleFilterChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          {ACTIVITY_FILTERS.map(filter => (
            <Tab key={filter.value} label={filter.label} value={filter.value} />
          ))}
        </Tabs>
      </Box>
      
      {/* Activities Feed */}
      <Feed>
        {paginatedActivities.length > 0 ? (
          paginatedActivities.map((activity, index) => {
            let field = JSON.parse(activity.field || "{}");

            return (
              <Feed.Event key={"activity-" + index}>
                <Feed.Label>
                  <Avatar>
                    {String(activity.userFullName)
                      .split(" ")
                      .map((part) => part.charAt(0))
                      .join("")
                      .toUpperCase()}
                  </Avatar>
                </Feed.Label>
                <Feed.Content>
                  <Feed.Date>{new Date(activity.createdAt).toLocaleString("en-us")}</Feed.Date>
                  {field.message ? (
                    <>
                      {getActivityHeader(field, activity, index)}
                      <Collapse in={!!showActivityDetail[index]}>
                        {!!Object.keys(field.details || {}).length && (
                          <Feed.Extra text>
                            <List bulleted={true}>
                              {Object.entries(field.details)
                                .filter(([fieldName, fieldValue]) => fieldValue)
                                .map(([fieldName, fieldValue]) => (
                                  <Box key={`${activity.id}_${fieldName}`}>
                                    <span style={{ textTransform: "capitalize", fontWeight: "600" }}>{fieldName}: </span>
                                    {fieldName === "certificateLink" ? (
                                      <a href={fieldValue} target="_blank">
                                        {fieldValue}
                                      </a>
                                    ) : (
                                      <div dangerouslySetInnerHTML={{ __html: fieldValue.toString().replace(/\n/g, `<br />`) }}></div>
                                    )}
                                  </Box>
                                ))}
                            </List>
                          </Feed.Extra>
                        )}
                      </Collapse>
                    </>
                  ) : (
                    <>
                      {getActivityHeader(field, activity, index)}
                      {!!Object.keys(field).length && (
                        <Feed.Extra text>
                          <List bulleted={true}>
                            {Object.entries(field).map(([fieldName, updates]) => (
                              <Box key={`${activity.id}_${fieldName}`}>
                                <p style={{ textTransform: "capitalize", fontWeight: "600" }}>{fieldName}</p>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                  {updates.prev ? (
                                    <p style={{ flexBasis: "50%", flexGrow: 0, overflowWrap: "anywhere" }}>{updates.prev?.toString()}</p>
                                  ) : (
                                    <p style={{ color: "darkgray" }}>{"blank"}</p>
                                  )}
                                  <ArrowForwardIcon sx={{ fontSize: 16, ml: 1, mr: 1 }} />
                                  {updates.next ? (
                                    <p style={{ flexBasis: "50%", flexGrow: 0, overflowWrap: "anywhere" }}>{updates.next?.toString()}</p>
                                  ) : (
                                    <p style={{ color: "darkgray" }}>{"blank"}</p>
                                  )}
                                </Box>
                              </Box>
                            ))}
                          </List>
                        </Feed.Extra>
                      )}
                    </>
                  )}
                </Feed.Content>
              </Feed.Event>
            );
          })
        ) : (
          <Typography variant="body1" color="text.secondary" align="center" my={4}>
            No activities found matching your filters.
          </Typography>
        )}
      </Feed>
      
      {/* Pagination */}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination 
            count={totalPages} 
            page={currentPage} 
            onChange={handlePageChange} 
            color="primary" 
          />
        </Box>
      )}
    </BoxShadow>
  );
}
